import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { getRouteApi } from '@tanstack/react-router';
import { useMutation } from '@tanstack/react-query';

import { APIcancelEmailChange, APIrequestEmailChange } from '../../api/User';

import Notices from '../Notices';

import { setNotice } from '../../helpers/notice';

import type { EmailChangeFormData } from '../../types/forms/emailchangeform';
import type { User } from '../../types/user';

const Email: React.FC = () => {
  const routeApi = getRouteApi('/account');
  const { profile } = routeApi.useLoaderData();
  const [pendingEmail, setPendingEmail] = useState<string | null>(profile.pending_email ?? null);

  const schema = z.object({
    email: z.email('Please enter a valid email address'),
    current_password: z.string().min(1, 'Current password is required'),
  }).refine((data) => data.email.toLowerCase() !== profile.email.toLowerCase(), {
    message: 'This is already your email address.',
    path: ['email'],
  });

  const {
    register,
    formState: {
      errors,
    },
    reset,
    setError,
    handleSubmit,
  } = useForm<EmailChangeFormData>({
    defaultValues: {
      email: '',
      current_password: '',
    },
    resolver: zodResolver(schema) as any,
  });

  const { mutate: requestEmailChange, isPending: isRequesting } = useMutation({
    mutationFn: (requestData: EmailChangeFormData) => APIrequestEmailChange(requestData),
    onError: (error: any) => {
      const fieldErrors: Record<string, string[]> = error?.errors ?? {};
      const fields = Object.keys(fieldErrors).filter((field): field is keyof EmailChangeFormData => field === 'email' || field === 'current_password');
      if (fields.length > 0) {
        fields.forEach((field) => setError(field, { message: fieldErrors[field][0] }));
      } else {
        setNotice('update.failed', error?.message);
      }
    },
    onSuccess: (user: User) => {
      setPendingEmail(user.pending_email ?? null);
      setNotice('email.change_requested');
      reset();
    },
  });

  const { mutate: cancelEmailChange, isPending: isCancelling } = useMutation({
    mutationFn: () => APIcancelEmailChange(),
    onError: (error: any) => {
      setNotice('update.failed', error?.message);
    },
    onSuccess: () => {
      setPendingEmail(null);
      setNotice('email.change_cancelled');
    },
  });

  const isPending = isRequesting || isCancelling;

  const submit = (data: EmailChangeFormData) => {
    requestEmailChange(data);
  };

  return (
    <div className="container account-container account-email-container">
      <div className="account-header page-header">
        <h2 className="page-title">Change <em>email</em></h2>
      </div>
      <div className="account-content account-email-content">
        <Notices />
        <p>
          Your current email address is <strong>{profile.email}</strong>.
        </p>
        {pendingEmail && (
          <wa-callout variant="warning" className="account-email-pending">
            <i className="fa-regular fa-envelope" slot="icon" />
            <div className="wa-split wa-gap-s">
              <span>
                Waiting for confirmation of <strong>{pendingEmail}</strong>. Check that inbox for the confirmation link.
              </span>
              <wa-button size="s" appearance="outlined" disabled={isPending} onClick={() => cancelEmailChange()}>
                {isCancelling ? 'Cancelling...' : 'Cancel change'}
              </wa-button>
            </div>
          </wa-callout>
        )}
        <p>
          We will send a confirmation link to your new email address. Your email will only change after you confirm it.
        </p>
        <form onSubmit={handleSubmit(submit)} noValidate>
          <wa-input
            label="New Email"
            type="email"
            autocomplete="email"
            disabled={isPending}
            className={errors.email ? 'invalid' : ''}
            hint={errors.email ? errors.email.message : ''}
            required
            {...register('email')}
          />
          <wa-input
            label="Current Password"
            type="password"
            autocomplete="current-password"
            disabled={isPending}
            className={errors.current_password ? 'invalid' : ''}
            hint={errors.current_password ? errors.current_password.message : ''}
            required
            {...register('current_password')}
          />
          <wa-button type="submit" disabled={isPending}>
            {isRequesting ? 'Sending...' : 'Send confirmation link'}
          </wa-button>
        </form>
      </div>
    </div>
  );
}

export default Email;
